import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import { Job } from '@/types';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { ImportJobsDialog } from '@/components/jobs/ImportJobsDialog';

export function JobsPage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [workLocationFilter, setWorkLocationFilter] = useState<string>('all');
  const [newJob, setNewJob] = useState<Partial<Job>>({
    title: '',
    job_location: '',
    type: 'full-time',
    work_location: 'office',
    description: '',
    requirements: [],
    status: 'draft'
  });
  const [newRequirement, setNewRequirement] = useState('');
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [editingRequirement, setEditingRequirement] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) {
        toast.error('Please set up your company profile first');
        return;
      }

      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('company_id', profile.company_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setNewJob(prev => ({
        ...prev,
        requirements: [...(prev.requirements || []), newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (index: number) => {
    setNewJob(prev => ({
      ...prev,
      requirements: prev.requirements?.filter((_, i) => i !== index) || []
    }));
  };

  const addEditingRequirement = () => {
    if (editingJob && editingRequirement.trim()) {
      setEditingJob(prev => {
        if (!prev) return null;
        return {
          ...prev,
          requirements: [...(prev.requirements || []), editingRequirement.trim()]
        };
      });
      setEditingRequirement('');
    }
  };

  const removeEditingRequirement = (index: number) => {
    if (editingJob) {
      setEditingJob(prev => {
        if (!prev) return null;
        return {
          ...prev,
          requirements: prev.requirements?.filter((_, i) => i !== index) || []
        };
      });
    }
  };

  const handleCreateJob = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) {
        toast.error('Please set up your company profile first');
        return;
      }

      const { error } = await supabase
        .from('jobs')
        .insert([{
          ...newJob,
          company_id: profile.company_id,
          requirements: newJob.requirements || []
        }]);

      if (error) throw error;

      toast.success('Job created successfully');
      setIsCreateDialogOpen(false);
      setNewJob({
        title: '',
        job_location: '',
        type: 'full-time',
        work_location: 'office',
        description: '',
        requirements: [],
        status: 'draft'
      });
      fetchJobs();
    } catch (error) {
      console.error('Error creating job:', error);
      toast.error('Failed to create job');
    }
  };

  const handleUpdateJob = async () => {
    if (!editingJob) return;

    try {
      const { error } = await supabase
        .from('jobs')
        .update({
          title: editingJob.title,
          job_location: editingJob.job_location,
          type: editingJob.type,
          work_location: editingJob.work_location,
          description: editingJob.description,
          requirements: editingJob.requirements || []
        })
        .eq('id', editingJob.id);

      if (error) throw error;

      toast.success('Job updated successfully');
      setIsEditDialogOpen(false);
      fetchJobs();
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error('Failed to update job');
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.job_location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    const matchesType = typeFilter === 'all' || job.type === typeFilter;
    const matchesWorkLocation = workLocationFilter === 'all' || job.work_location === workLocationFilter;
    return matchesSearch && matchesStatus && matchesType && matchesWorkLocation;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-500';
      case 'draft':
        return 'bg-yellow-500';
      case 'closed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Job Listings</h1>
        <div className="space-x-2">
          <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
            Import Jobs
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            Create Job
          </Button>
        </div>
      </div>

      <ImportJobsDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onImportComplete={fetchJobs}
      />

      <div className="flex items-center space-x-4">
        <Input
          placeholder="Search jobs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="full-time">Full Time</SelectItem>
            <SelectItem value="part-time">Part Time</SelectItem>
            <SelectItem value="contract">Contract</SelectItem>
          </SelectContent>
        </Select>
        <Select value={workLocationFilter} onValueChange={setWorkLocationFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Work Location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            <SelectItem value="remote">Remote</SelectItem>
            <SelectItem value="office">Office</SelectItem>
            <SelectItem value="hybrid">Hybrid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-10">Loading jobs...</div>
      ) : filteredJobs.length === 0 ? (
        <div className="text-center py-10">No jobs found</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredJobs.map((job) => (
            <div key={job.id} className="border rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">{job.title}</h3>
                <Badge 
                  className={getStatusColor(job.status)}
                >
                  {job.status}
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{job.job_location}</p>
                <div className="flex space-x-2">
                  <Badge variant="secondary">{job.type}</Badge>
                  <Badge variant="outline">{job.work_location}</Badge>
                </div>
              </div>
              <div className="text-sm line-clamp-3">{job.description}</div>
              {job.requirements && job.requirements.length > 0 && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium">Requirements:</h4>
                  <ul className="list-disc list-inside text-sm text-muted-foreground">
                    {job.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end space-x-2 pt-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setEditingJob(job);
                    setIsEditDialogOpen(true);
                  }}
                >
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Job Creation Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Create New Job</DialogTitle>
            <DialogDescription>
              Fill in the details for the new job posting
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Title</label>
                <Input
                  value={newJob.title}
                  onChange={(e) => setNewJob(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Location</label>
                <Input
                  value={newJob.job_location}
                  onChange={(e) => setNewJob(prev => ({ ...prev, job_location: e.target.value }))}
                  placeholder="e.g., New York, NY"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Type</label>
                <Select
                  value={newJob.type}
                  onValueChange={(value) => setNewJob(prev => ({ ...prev, type: value as Job['type'] }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select job type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time">Full Time</SelectItem>
                    <SelectItem value="part-time">Part Time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Work Location</label>
                <Select
                  value={newJob.work_location}
                  onValueChange={(value) => setNewJob(prev => ({ ...prev, work_location: value as Job['work_location'] }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select work location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <textarea
                className="w-full min-h-[100px] p-2 rounded-md border"
                value={newJob.description}
                onChange={(e) => setNewJob(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter job description..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Requirements</label>
              <div className="flex space-x-2">
                <Input
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                  placeholder="Enter job requirement"
                  className="flex-grow"
                />
                <Button 
                  variant="outline" 
                  onClick={addRequirement}
                  disabled={!newRequirement.trim()}
                >
                  Add
                </Button>
              </div>
              {newJob.requirements && newJob.requirements.length > 0 && (
                <div className="mt-2 space-y-1">
                  {newJob.requirements.map((req, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between bg-gray-100 p-2 rounded"
                    >
                      <span>{req}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => removeRequirement(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateJob}>Create Job</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Editing Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Edit Job</DialogTitle>
            <DialogDescription>
              Update the details of the job posting
            </DialogDescription>
          </DialogHeader>
          {editingJob && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Title</label>
                  <Input
                    value={editingJob.title}
                    onChange={(e) => setEditingJob(prev => prev ? { ...prev, title: e.target.value } : null)}
                    placeholder="e.g., Senior Software Engineer"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Location</label>
                  <Input
                    value={editingJob.job_location}
                    onChange={(e) => setEditingJob(prev => prev ? { ...prev, job_location: e.target.value } : null)}
                    placeholder="e.g., New York, NY"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Type</label>
                  <Select
                    value={editingJob.type}
                    onValueChange={(value) => setEditingJob(prev => prev ? { ...prev, type: value as Job['type'] } : null)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select job type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full-time">Full Time</SelectItem>
                      <SelectItem value="part-time">Part Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Work Location</label>
                  <Select
                    value={editingJob.work_location}
                    onValueChange={(value) => setEditingJob(prev => prev ? { ...prev, work_location: value as Job['work_location'] } : null)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select work location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="remote">Remote</SelectItem>
                      <SelectItem value="office">Office</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <textarea
                  className="w-full min-h-[100px] p-2 rounded-md border"
                  value={editingJob.description}
                  onChange={(e) => setEditingJob(prev => prev ? { ...prev, description: e.target.value } : null)}
                  placeholder="Enter job description..."
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Requirements</label>
                <div className="flex space-x-2">
                  <Input
                    value={editingRequirement}
                    onChange={(e) => setEditingRequirement(e.target.value)}
                    placeholder="Enter job requirement"
                    className="flex-grow"
                  />
                  <Button 
                    variant="outline" 
                    onClick={addEditingRequirement}
                    disabled={!editingRequirement.trim()}
                  >
                    Add
                  </Button>
                </div>
                {editingJob.requirements && editingJob.requirements.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {editingJob.requirements.map((req, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between bg-gray-100 p-2 rounded"
                      >
                        <span>{req}</span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => removeEditingRequirement(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateJob}>Update Job</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}